import type { Renderer, Spritesheet, Texture } from "pixi.js";
import type { BrandedAssetResolver } from "../branding/BrandedAssetResolver";
import type { Log } from "@slot/presentation";

/**
 * Resolves atlas art to data URLs the DOM chrome paints as images: `resolve` takes the info page's
 * `<img src>` URIs, `textureToDataUrl` a loaded texture (the purchase offer icons, the intro splash
 * art). The chrome's own frames never come here: `chromeArt.ts` places the atlas by CSS. The URIs:
 *
 * - `bundle://<bundle>/<atlas>#<frame>`  a frame of a loaded spritesheet (the paytable atlas, the
 *   symbols atlas). The shared bundle goes through `BrandedAssetResolver`, so an operator atlas
 *   listed in the manifest replaces the whole atlas (parity).
 * - `bundle://<bundle>/<path>`             a standalone texture (`/spriteFrame` suffix tolerated).
 * - `inline://<key>#<frame>`               a frame of a shell-owned atlas (`infoPage/icons`); not
 *   brandable.
 *
 * Frames are rasterised once through the renderer (trimmed / rotated atlas frames come out as the
 * plain image) and cached per URI.
 */
export class ChromeImageResolver {
  private readonly cache = new Map<string, Promise<string | null>>();

  public constructor(
    private readonly input: {
      renderer: Renderer;
      branded: BrandedAssetResolver;
      sharedBundleName: string;
      inlineAtlases: ReadonlyMap<string, Spritesheet>;
      log: Log;
    },
  ) {}

  public resolve(src: string): Promise<string | null> {
    let pending = this.cache.get(src);
    if (!pending) {
      pending = this.load(src);
      this.cache.set(src, pending);
    }
    return pending;
  }

  /** A loaded texture as a PNG data URL: the purchase offer icons and the intro splash art, which have no URI. */
  public async textureToDataUrl(texture: Texture): Promise<string> {
    return this.input.renderer.extract.base64({ target: texture, format: "png" });
  }

  private async load(src: string): Promise<string | null> {
    const texture = this.findTexture(src);
    if (!texture) return null;
    try {
      return await this.textureToDataUrl(texture);
    } catch (error) {
      this.input.log(`[ChromeImageResolver] failed to rasterise "${src}": ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  }

  private findTexture(src: string): Texture | null {
    if (src.startsWith("inline://")) {
      const parsed = splitFragment(src.slice("inline://".length));
      if (!parsed.fragment) return this.warn(src, "malformed inline uri");
      const atlas = this.input.inlineAtlases.get(parsed.path);
      if (!atlas) return this.warn(src, `inline atlas "${parsed.path}" not registered`);
      return atlas.textures[parsed.fragment] ?? this.warn(src, `inline atlas "${parsed.path}" has no frame "${parsed.fragment}"`);
    }
    if (!src.startsWith("bundle://")) return this.warn(src, "unsupported uri scheme");
    const parsed = splitFragment(src.slice("bundle://".length));
    const slash = parsed.path.indexOf("/");
    if (slash <= 0) return this.warn(src, "malformed bundle uri");
    const bundle = parsed.path.slice(0, slash);
    const rawPath = parsed.path.slice(slash + 1);
    if (rawPath.length === 0) return this.warn(src, "malformed bundle uri");
    const lookup = <T>(path: string): T | null =>
      bundle === this.input.sharedBundleName ? this.input.branded.getAssetOrNull<T>(path) : this.input.branded.getFromBundleOrNull<T>(bundle, path);
    if (parsed.fragment) {
      const atlas = lookup<Spritesheet>(rawPath);
      if (!atlas || !atlas.textures) return this.warn(src, `atlas "${rawPath}" not loaded from "${bundle}"`);
      return atlas.textures[parsed.fragment] ?? this.warn(src, `atlas "${rawPath}" has no frame "${parsed.fragment}"`);
    }
    // Authored sprite paths may carry the `/spriteFrame` sub-asset suffix; the asset aliases do not.
    const path = rawPath.endsWith("/spriteFrame") ? rawPath.slice(0, -"/spriteFrame".length) : rawPath;
    return lookup<Texture>(path) ?? this.warn(src, `texture "${path}" not loaded from "${bundle}"`);
  }

  private warn(src: string, reason: string): null {
    this.input.log(`[ChromeImageResolver] ${reason} for "${src}"`);
    return null;
  }
}

function splitFragment(value: string): { path: string; fragment: string | null } {
  const hash = value.indexOf("#");
  if (hash < 0) return { path: value, fragment: null };
  const fragment = value.slice(hash + 1);
  return { path: value.slice(0, hash), fragment: fragment.length > 0 ? fragment : null };
}
